import { useState, useEffect } from 'react'
import { useNavigate, Outlet, useLocation } from 'react-router-dom'
import { supabase } from './supabaseClient'
import './App.css'
import type { Session } from '@supabase/supabase-js' // Type-only import for Session
import posthog from 'posthog-js'
import { toast } from 'sonner'
import LoadingScreen from './components/LoadingScreen'

// This component handles auth state and redirects
function AuthLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const hash = window.location.hash
    if (hash.includes('error_description')) {
      const params = new URLSearchParams(hash.substring(1))
      const errorDescription = params.get('error_description')
      if (errorDescription) {
        toast.error(decodeURIComponent(errorDescription))
        window.history.replaceState(null, '', window.location.pathname)
      }
    }

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession)
      setLoading(false)
    })

    return () => {
      authListener?.subscription?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!loading) {
      if (session?.user) {
        if (location.pathname === '/login' || location.pathname === '/signup') {
          navigate('/runs');
        } else if (location.pathname === '/') {
          navigate('/runs');
        }
      } else {
        if (location.pathname !== '/login' && location.pathname !== '/signup') {
          navigate('/login');
        }
      }
    }
  }, [session, loading, navigate, location.pathname])

  useEffect(() => {
    if (session?.user) {
      const user = session.user
      const isNewUser = !user.last_sign_in_at || (new Date(user.created_at).getTime() === new Date(user.last_sign_in_at).getTime())

      posthog.identify(user.id, {
        email: user.email,
        name: user.user_metadata?.full_name,
      });

      if (isNewUser) {
        posthog.capture('user_signed_up', {
          method: user.app_metadata.provider || 'password',
        });
      }
      posthog.capture('user_logged_in', {
        method: user.app_metadata.provider || 'password',
      });
    } else {
      posthog.reset();
    }
  }, [session])

  if (loading) {
    return <LoadingScreen />
  }

  return <Outlet />
}

export { AuthLayout }

